import type { Edge } from '../../../services/gql/types';
import type {
  AssetDisplayType,
  AssetLevel,
  ModelArtifactType,
} from '../../../types/types';
import type {
  ApiLineage,
  ApiLineageNode,
  LineageLatestTestExecution,
} from '../types';
import {
  isRestrictedApiLineageNode,
  mapLatestTestExecutions,
} from './lineageApiParsing';

/**
 * Narrow lineage node shape selected by controlled-mode GraphQL queries
 * (incident lineage, etc.). Mirrors the fields consumed by `useLineageApi`.
 */
export type GqlLineageNode = {
  id: string;
  __typename?: string;
  name: string;
  displayName?: string | null;
  parentId?: string | null;
  artifactType: ModelArtifactType;
  displayType: AssetDisplayType;
  sourceType?: string;
  level?: AssetLevel;
  tags: string[];
  owners: string[];
  path?: string | null;
  parent?: { id: string };
  platform?: string | null;
  biType?: string | null;
  semanticType?: string | null;
  dataPlatform?: string | null;
  columnsCount?: number;
  latestTestExecutions?: LineageLatestTestExecution[];
};

export type GqlLineageData = {
  assets: { nodes: GqlLineageNode[]; edges: Edge[] };
  columns?: {
    nodes: GqlLineageNode[];
    edges: Edge[];
  };
};

const toApiAssetNode = (node: GqlLineageNode): ApiLineageNode => ({
  id: node.id,
  isRestricted: isRestrictedApiLineageNode(node),
  type: node.artifactType,
  displayType: node.displayType,
  sourceType: node.sourceType,
  artifactType: node.artifactType,
  // Mirror useLineageApi: only SUB_NODE asset nodes nest under a parent.
  parent: node.level === 'SUB_NODE' ? node.parentId ?? undefined : undefined,
  level: node.level,
  label: node.displayName || node.name,
  tags: node.tags,
  owners: node.owners,
  databaseName: undefined,
  schemaName: undefined,
  tableName: undefined,
  path: node.path === null ? undefined : node.path,
  platform: node.platform ?? undefined,
  subType: node.biType ?? node.semanticType ?? undefined,
  dataPlatform: node.dataPlatform ?? undefined,
  columnsCount: node.columnsCount,
  testExecutions: mapLatestTestExecutions(node.latestTestExecutions),
});

const toApiColumnNode = (node: GqlLineageNode): ApiLineageNode => {
  const parentFromColumn = node.parent?.id;
  const parentId = parentFromColumn ?? node.parentId;

  return {
    id: node.id,
    isRestricted: isRestrictedApiLineageNode(node),
    type: node.artifactType,
    displayType: node.displayType,
    sourceType: node.sourceType,
    artifactType: node.artifactType,
    parent: parentId ?? undefined,
    level: node.level,
    label: node.displayName || node.name,
    tags: node.tags,
    owners: node.owners,
    databaseName: undefined,
    schemaName: undefined,
    tableName: undefined,
    path: node.path === null ? undefined : node.path,
    platform: undefined,
    subType: undefined,
    dataPlatform: undefined,
  };
};

/**
 * Merges GQL lineage (assets + columns subgraphs) into the shape consumed by the lineage worker.
 * Same output shape as `useLineageApi`.
 */
export const mapGqlLineageToApiLineage = (
  lineage: GqlLineageData,
): ApiLineage => {
  const byId = new Map<string, ApiLineageNode>();

  // Mirror useLineageApi: COLLECTION-level asset nodes (e.g. Sigma workbooks)
  // are aggregation containers and should not appear as graph nodes.
  for (const n of lineage.assets.nodes) {
    if (n.level === 'COLLECTION') continue;
    byId.set(n.id, toApiAssetNode(n));
  }
  for (const n of lineage.columns?.nodes ?? []) {
    byId.set(n.id, toApiColumnNode(n));
  }

  const edgeKey = (e: { src: string; dst: string }) => `${e.src}->${e.dst}`;
  const edgeSeen = new Set<string>();
  const edges: [string, string][] = [];

  for (const e of [
    ...lineage.assets.edges,
    ...(lineage.columns?.edges ?? []),
  ]) {
    const k = edgeKey(e);
    if (!edgeSeen.has(k)) {
      edgeSeen.add(k);
      edges.push([e.dst, e.src]);
    }
  }

  return {
    nodes: [...byId.values()],
    edges,
  };
};
